import React, { useState, useEffect, useRef } from 'react';
import { pickRandomSplash } from '../../data/splashData';
import './LoadingSplash.css';

/**
 * LoadingSplash
 * 
 * Exibe a tela de loading com:
 * - Imagem de fundo de um personagem histórico (aleatória, sem repetição consecutiva)
 * - Nome do personagem
 * - Frase de efeito
 * - Spinner + texto "Carregando memórias ancestrais..."
 * 
 * A imagem é buscada de /assets/splash/splash_XX.webp.
 * Se a imagem não existir ainda, exibe fallback com fundo escuro + estrelas.
 * 
 * Para alterar frases ou personagens, edite src/data/splashData.js.
 */

// Base path das imagens splash — ajustado dinamicamente para subpasta no deploy
const SPLASH_BASE_PATH = (process.env.PUBLIC_URL || (typeof window !== 'undefined' && window.location.pathname.includes('/memoria-dos-imortais') ? '/memoria-dos-imortais' : '')).replace(/\/$/, '') + '/assets/splash/';

const LoadingSplash = ({ onComplete }) => {
  // Sorteia 1 personagem no mount (e só uma vez por render)
  const splashRef = useRef(pickRandomSplash());
  const splash = splashRef.current;

  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [timeLeft, setTimeLeft] = useState(5);

  const imageUrl = `${SPLASH_BASE_PATH}${splash.file}`;

  // Pré-carrega a imagem antes de exibir para evitar flicker
  useEffect(() => {
    const img = new Image();
    img.src = imageUrl;
    img.onload = () => setImageLoaded(true);
    img.onerror = () => setImageError(true); // fallback gracioso
  }, [imageUrl]);

  // Temporizador de 5 segundos com callback para prosseguir
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          if (onComplete) onComplete();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onComplete]);

  const handleSkip = () => {
    if (onComplete) onComplete();
  };

  const noImage = imageError || !splash.file;

  return (
    <div
      className={`loading-splash ${noImage ? 'splash-no-image' : ''}`}
      role="status"
      aria-label="Carregando jogo"
    >
      {/* Botão Pular */}
      <button className="splash-skip-btn" onClick={handleSkip} aria-label="Pular tela de transição">
        Pular <span className="skip-arrow">➔</span> ({timeLeft}s)
      </button>

      {/* Container da imagem de fundo com desfoque e centralização perfeita */}
      {!imageError && (
        <div className="splash-bg-container">
          <img
            src={imageUrl}
            alt=""
            className="splash-bg-blur"
            aria-hidden="true"
          />
          <img
            src={imageUrl}
            alt={splash.nome}
            className={`splash-bg-image ${imageLoaded ? 'loaded' : ''}`}
            aria-hidden="true"
          />
        </div>
      )}

      {/* Overlay gradiente para legibilidade */}
      <div className="splash-overlay" aria-hidden="true" />

      {/* Conteúdo: nome + frase + spinner */}
      <div className="splash-content">
        {/* Nome do personagem */}
        <p
          className="splash-character-name"
          style={{ color: splash.cor || '#d4af37' }}
        >
          {splash.nome}
        </p>

        {/* Frase de efeito */}
        <p className="splash-quote">"{splash.frase}"</p>

        {/* Divisor */}
        <div className="splash-divider" aria-hidden="true" />

        {/* Spinner + texto */}
        <div className="splash-loading-row">
          <div className="splash-spinner" aria-hidden="true" />
          <span className="splash-loading-text">
            Carregando memórias ancestrais...
          </span>
        </div>
      </div>
    </div>
  );
};

export default LoadingSplash;
